import {FC, useEffect, useState} from 'react'
import {api, ApiError, PaymentSettings} from '../../services/api'
import {Spinner} from '../../components/Spinner'
import {PageHead} from '../../components/PageHead'
import {useToast} from '../../components/Toast'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  fontSize: 14,
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
}

const Card: FC<{title: string; sub?: string; right?: React.ReactNode; children: React.ReactNode}> = ({title, sub, right, children}) => (
  <div className='ccp-lift' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #eef0f4', padding: '24px 26px', marginBottom: 18}}>
    <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12}}>
      <div>
        <h2 style={{fontSize: 18, fontWeight: 800, color: INK, margin: 0}}>{title}</h2>
        {sub && <p style={{fontSize: 13.5, color: MUTED, margin: '4px 0 0'}}>{sub}</p>}
      </div>
      {right}
    </div>
    <div style={{height: 18}} />
    {children}
  </div>
)

const Notice: FC<{kind: 'ok' | 'err'; children: React.ReactNode}> = ({kind, children}) => (
  <div style={{borderRadius: 9, padding: '10px 14px', fontSize: 14, marginBottom: 16, ...(kind === 'ok' ? {background: 'rgba(3,149,64,0.1)', color: '#1f9d5b'} : {background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626'})}}>
    {children}
  </div>
)

const Label: FC<{children: React.ReactNode}> = ({children}) => (
  <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6}}>{children}</label>
)

const btn = (disabled: boolean): React.CSSProperties => ({
  fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none',
  borderRadius: 10, padding: '12px 26px', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.7 : 1,
})

/** A simple on/off pill toggle. */
const Toggle: FC<{on: boolean; onChange: (v: boolean) => void; label: string}> = ({on, onChange, label}) => (
  <button
    type='button'
    onClick={() => onChange(!on)}
    style={{display: 'inline-flex', alignItems: 'center', gap: 9, background: 'none', border: 'none', cursor: 'pointer', padding: 0}}
  >
    <span style={{width: 42, height: 24, borderRadius: 999, background: on ? ACCENT : '#cbd5e1', position: 'relative', transition: 'background .15s'}}>
      <span style={{position: 'absolute', top: 2, left: on ? 20 : 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'}} />
    </span>
    <span style={{fontSize: 13, fontWeight: 700, color: on ? ACCENT : MUTED}}>{on ? `${label} ON` : `${label} OFF`}</span>
  </button>
)

const UNITS = ['minute', 'hour', 'day', 'week', 'month']

/** Number + unit picker for an auto-charge interval. */
const IntervalPicker: FC<{value: number; unit: string; onValue: (n: number) => void; onUnit: (u: string) => void}> = ({value, unit, onValue, onUnit}) => (
  <div style={{display: 'flex', gap: 10}}>
    <input type='number' min={1} style={{...inputStyle, width: 90}} value={value} onChange={(e) => onValue(Math.max(1, parseInt(e.target.value || '1', 10)))} />
    <select style={{...inputStyle, width: 150}} value={unit} onChange={(e) => onUnit(e.target.value)}>
      {UNITS.map((u) => <option key={u} value={u}>{u}{value === 1 ? '' : 's'}</option>)}
    </select>
  </div>
)

const PaymentSettingsPage: FC = () => {
  const toast = useToast()
  const [s, setS] = useState<PaymentSettings | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    api.getPaymentSettings().then(setS).catch((e) => setLoadErr(e instanceof ApiError ? e.message : 'Could not load settings'))
  }, [])

  const set = (patch: Partial<PaymentSettings>) => setS((cur) => (cur ? {...cur, ...patch} : cur))

  const save = async () => {
    if (!s) return
    setSaving(true)
    try {
      const saved = await api.updatePaymentSettings({
        zelleEnabled: s.zelleEnabled,
        zelleName: s.zelleName,
        zelleHandle: s.zelleHandle,
        zelleInstructions: s.zelleInstructions ?? '',
        stripeEnabled: s.stripeEnabled,
        stripePublishableKey: s.stripePublishableKey ?? '',
        stripeSecretKey: s.stripeSecretKey ?? '',
        stripeWebhookSecret: s.stripeWebhookSecret ?? '',
        notifyEmail: s.notifyEmail ?? '',
        depositAmount: s.depositAmount,
        rentAutoEnabled: s.rentAutoEnabled,
        rentIntervalValue: s.rentIntervalValue,
        rentIntervalUnit: s.rentIntervalUnit,
        bedWashAutoEnabled: s.bedWashAutoEnabled,
        bedWashIntervalValue: s.bedWashIntervalValue,
        bedWashIntervalUnit: s.bedWashIntervalUnit,
        bedWashFee: s.bedWashFee,
      })
      setS(saved)
      toast.success('Payment settings saved.')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not save settings')
    } finally {
      setSaving(false)
    }
  }

  if (loadErr) return <div><PageHead icon='💳' eyebrow='Money' title='Payment Settings' /><Notice kind='err'>{loadErr}</Notice></div>
  if (!s) return <div><PageHead icon='💳' eyebrow='Money' title='Payment Settings' /><Spinner size={20} /></div>

  return (
    <div>
      <PageHead icon='💳' eyebrow='Money' title='Payment Settings' sub='Choose how renters pay the deposit, rent, and bed washes.' />

      <div className='ccp-stagger'>
        <Card title='General'>
          <div className='row g-3'>
            <div className='col-md-6'>
              <Label>Deposit amount (USD)</Label>
              <input style={inputStyle} value={s.depositAmount} onChange={(e) => set({depositAmount: e.target.value})} placeholder='100.00' />
            </div>
            <div className='col-md-6'>
              <Label>Notify email (owner alerted on each payment)</Label>
              <input style={inputStyle} value={s.notifyEmail ?? ''} onChange={(e) => set({notifyEmail: e.target.value})} placeholder='owner@example.com' />
            </div>
          </div>
        </Card>

        {s.zelleAdminEnabled && (
        <Card
          title='Zelle'
          sub='No processor fees. The renter sends the deposit; you (or the recipient) get an email, then an admin confirms it.'
          right={<Toggle on={s.zelleEnabled} onChange={(v) => set({zelleEnabled: v})} label='Zelle' />}
        >
          <div className='row g-3'>
            <div className='col-md-6'>
              <Label>Recipient name</Label>
              <input style={inputStyle} value={s.zelleName} onChange={(e) => set({zelleName: e.target.value})} placeholder='Crosscheck Crashpad' />
            </div>
            <div className='col-md-6'>
              <Label>Zelle-linked email or phone (receives deposits)</Label>
              <input style={inputStyle} value={s.zelleHandle} onChange={(e) => set({zelleHandle: e.target.value})} placeholder='pay@crosscheckcrashpad.com' />
            </div>
            <div className='col-12'>
              <Label>Instructions shown to the renter</Label>
              <textarea style={{...inputStyle, minHeight: 84, resize: 'vertical'}} value={s.zelleInstructions ?? ''} onChange={(e) => set({zelleInstructions: e.target.value})} />
            </div>
          </div>
        </Card>
        )}

        {s.stripeAdminEnabled && (
        <Card
          title='Stripe (card)'
          sub='Instant card payments. Paste your keys from the Stripe dashboard. When on, the deposit is recorded automatically once paid.'
          right={<Toggle on={s.stripeEnabled} onChange={(v) => set({stripeEnabled: v})} label='Stripe' />}
        >
          <div className='row g-3'>
            <div className='col-md-6'>
              <Label>Publishable key (pk_…)</Label>
              <input style={inputStyle} value={s.stripePublishableKey ?? ''} onChange={(e) => set({stripePublishableKey: e.target.value})} placeholder='pk_live_… or pk_test_…' />
            </div>
            <div className='col-md-6'>
              <Label>Secret key (sk_…)</Label>
              <input type='password' style={inputStyle} value={s.stripeSecretKey ?? ''} onChange={(e) => set({stripeSecretKey: e.target.value})} placeholder='sk_live_… or sk_test_…' />
            </div>
            <div className='col-md-6'>
              <Label>Webhook signing secret (whsec_…)</Label>
              <input type='password' style={inputStyle} value={s.stripeWebhookSecret ?? ''} onChange={(e) => set({stripeWebhookSecret: e.target.value})} placeholder='whsec_…' />
            </div>
          </div>
          <p style={{fontSize: 12.5, color: MUTED, margin: '14px 0 0', lineHeight: 1.6}}>
            Point your Stripe webhook at <code>/api/payments/stripe/webhook</code> and subscribe to the
            {' '}<strong>checkout.session.completed</strong> event, then paste its signing secret above.
          </p>
        </Card>
        )}

        <Card
          title='Auto-charge (rent & bed wash)'
          sub='Automatically charge the card on file on a set interval. Handy for testing — you can set it to minutes or hours. Both are off by default.'
        >
          {/* Rent */}
          <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10}}>
            <Label>Rent — charge the monthly rate every…</Label>
            <Toggle on={s.rentAutoEnabled} onChange={(v) => set({rentAutoEnabled: v})} label='Rent auto' />
          </div>
          <IntervalPicker value={s.rentIntervalValue} unit={s.rentIntervalUnit} onValue={(n) => set({rentIntervalValue: n})} onUnit={(u) => set({rentIntervalUnit: u})} />
          <p style={{fontSize: 12.5, color: MUTED, margin: '8px 0 0', lineHeight: 1.6}}>
            Charges each active booking’s monthly rate off-session to the saved card. Real Stripe subscriptions can’t bill under a day — this sweep can, for testing.
          </p>

          {/* Bed wash */}
          <div style={{borderTop: '1px solid #f1f3f7', margin: '18px 0 0', paddingTop: 18}}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10}}>
              <Label>Bed wash — auto-wash &amp; charge every…</Label>
              <Toggle on={s.bedWashAutoEnabled} onChange={(v) => set({bedWashAutoEnabled: v})} label='Bed wash auto' />
            </div>
            <div style={{display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end'}}>
              <IntervalPicker value={s.bedWashIntervalValue} unit={s.bedWashIntervalUnit} onValue={(n) => set({bedWashIntervalValue: n})} onUnit={(u) => set({bedWashIntervalUnit: u})} />
              <div>
                <Label>Fee (USD)</Label>
                <input style={{...inputStyle, width: 110}} value={s.bedWashFee} onChange={(e) => set({bedWashFee: e.target.value})} placeholder='5.00' />
              </div>
            </div>
            <p style={{fontSize: 12.5, color: MUTED, margin: '8px 0 0', lineHeight: 1.6}}>
              Any rented bed not washed within this window is charged the fee and added to the housekeeping queue.
            </p>
          </div>
        </Card>

        <button onClick={save} disabled={saving} style={btn(saving)}>
          {saving ? <Spinner size={14} /> : 'Save payment settings'}
        </button>
      </div>
    </div>
  )
}

export {PaymentSettingsPage}
